"use client";

import {
  Box,
  Collapse,
  Flex,
  Icon,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { HiOutlineChevronDown, HiOutlineClock } from "react-icons/hi2";
import { TouchAwareTooltip } from "@/components/ui";
import { useTenantTimeZone, useTickingNow } from "@/hooks/shared/useStudioClock";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";
import {
  formatStudioClockFace,
  getStudioTimeZoneHintLines,
  getStudioTimeZoneIanaId,
  shouldShowStudioTimeZoneHint,
} from "@/lib/utils/date/studio-time-zone-display";

type StudioTimeZoneHintProps = {
  /** Sidebar: icon-only row when nav is collapsed. */
  isCollapsed?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  "data-testid"?: string;
};

/**
 * Studio wall clock — only when the device is not in the studio zone.
 */
export function StudioTimeZoneHint({
  isCollapsed = false,
  onVisibilityChange,
  "data-testid": dataTestId = "studio-timezone-hint",
}: StudioTimeZoneHintProps) {
  const theme = useThemeTokens();
  const { controlBorderRadius } = useClassicDashboardStyles();
  const studioTimeZone = useTenantTimeZone();
  const now = useTickingNow();
  const { isOpen, onToggle } = useDisclosure();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const visible =
    isClient && shouldShowStudioTimeZoneHint(studioTimeZone, undefined, now);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);
  const face = useMemo(
    () => formatStudioClockFace(now, studioTimeZone),
    [now, studioTimeZone],
  );
  const lines = useMemo(
    () => getStudioTimeZoneHintLines(studioTimeZone, "default"),
    [studioTimeZone],
  );
  const studioIanaId = useMemo(
    () => getStudioTimeZoneIanaId(studioTimeZone),
    [studioTimeZone],
  );

  if (!visible || !face || !lines) return null;

  const summary = [face.date, face.time, face.zone].filter(Boolean).join(" · ");
  const hoverBg =
    theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(11,13,16,0.05)";

  if (isCollapsed) {
    return (
      <TouchAwareTooltip label={summary} placement="right" openDelay={200}>
        <Flex
          align="center"
          justify="center"
          w="40px"
          py={2}
          mx="auto"
          color={theme.text.tertiary}
          data-testid={dataTestId}
          aria-label={summary}
        >
          <Icon as={HiOutlineClock} boxSize={4} />
        </Flex>
      </TouchAwareTooltip>
    );
  }

  return (
    <Box w="100%" py={1} data-testid={dataTestId}>
      <Flex
        as="button"
        type="button"
        align="center"
        gap={3}
        w="100%"
        px={3}
        py={2}
        borderRadius={controlBorderRadius}
        bg="transparent"
        color={theme.text.tertiary}
        textAlign="left"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={summary}
        _hover={{
          bg: hoverBg,
          color: theme.text.secondary,
        }}
      >
        <Icon as={HiOutlineClock} boxSize={3.5} flexShrink={0} opacity={0.72} />
        <Box flex="1" minW={0}>
          <Text
            fontSize="sm"
            fontWeight="500"
            letterSpacing="0.02em"
            lineHeight="1.2"
            sx={{ fontVariantNumeric: "tabular-nums" }}
            color={theme.text.secondary}
          >
            {face.time}
          </Text>
          <Text
            mt="3px"
            fontSize="2xs"
            fontWeight="500"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            noOfLines={1}
          >
            {face.date}
            {face.zone ? ` · ${face.zone}` : ""}
          </Text>
        </Box>
        <Icon
          as={HiOutlineChevronDown}
          boxSize={3}
          flexShrink={0}
          opacity={0.55}
          transition="transform 0.2s"
          transform={isOpen ? "rotate(180deg)" : undefined}
        />
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <Box
          px={3}
          pt={1.5}
          pb={1}
          pl="38px"
          fontSize="xs"
          color={theme.text.secondary}
          lineHeight="1.45"
        >
          <Text>{lines.primary}</Text>
          {studioIanaId ? (
            <Text
              mt={1}
              fontSize="2xs"
              color={theme.text.tertiary}
              fontFamily="mono"
              letterSpacing="0.01em"
            >
              {studioIanaId}
            </Text>
          ) : null}
          {lines.secondary ? (
            <Text color={theme.text.tertiary} mt={1.5}>
              {lines.secondary}
            </Text>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
